import {Type} from '@mintdata/mint-kit/api/types/Type';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {
    FieldName,
    FrameData,
    FrameMetaKey,
    FrameMetaValue,
    FramePath,
    IFrame,
    RowIndex
} from '@mintdata/mint-kit/api/values/frames/Frame';
import {InvalidFrameEncoding} from '@mintdata/mint-kit/api/values/frames/FrameErrors';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {FrameMeta} from '@mintdata/mint-kit/api/values/frames/FrameMeta';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';

export enum JoinType
{
    INNER,
    LEFT,
    RIGHT,
    FULL
}

export class Frames
{
    public static readonly META_KEY = 'meta';

    public static empty<T>():IFrame
    {
        return Frames.fromStrings([], []);
    }

    public static fromJSON(jsonString:string):IFrame
    {
        let parsedJSONObject;

        try
        {
            parsedJSONObject = JSON.parse(jsonString);
        }
        catch( e )
        {
            throw new InvalidFrameEncoding(`Could not parse frame; invalid JSON string: ${jsonString}`);
        }

        return Frames.fromJSONObject(parsedJSONObject);
    }

    public static deserializeFrameFromApi(frameJsonFromApi:any):IFrame | AError
    {
        try
        {
            return Frames.fromJSONObject(frameJsonFromApi);
        }
        catch( e )
        {
            return new AError('#DESERIALIZATION_ERROR');
        }
    }

    public static fromJSONObject(parsedJsonObject:any):IFrame
    {
        const {deserialize} = require('@mintdata/mint-kit/impl/util/serializr/src/serializr');

        if( AError.isSerializedErrorValue(parsedJsonObject) )
            return deserialize(AError, parsedJsonObject);

        const fieldNames = Object.keys(parsedJsonObject).filter(
            (fieldName:FieldName) => fieldName !== Frames.META_KEY && fieldName !== Type.TYPE_KEY
        );

        if( !fieldNames )
            throw new InvalidFrameEncoding(`Could not parse frame; JSON object has 0 keys`);

        if( fieldNames.length === 0 )
            return Frames.empty();

        let numRows = -1;

        fieldNames.map((fieldName:FieldName) =>
        {
            const oColumn = parsedJsonObject[fieldName];
            const oColumnRowValues = oColumn.rows;

            if( !oColumnRowValues )
                throw new InvalidFrameEncoding(`Column ${fieldName} is missing a .rows field with the column's cells`);

            if( !Array.isArray(oColumnRowValues) )
                throw new InvalidFrameEncoding(`Column ${fieldName} has a .rows field which is not a JavaScript array ([])`);

            const curColumnNumRows = oColumnRowValues.length;

            if( numRows === -1 )
                numRows = curColumnNumRows;
            else
            {
                if( curColumnNumRows != numRows )
                    throw new InvalidFrameEncoding(`Each column for frame must have the same number of rows ` +
                        `(# rows encountered: ${curColumnNumRows}` +
                        ` previously # rows for each column was: ${numRows})`);
            }
        });

        const fields:Array<FrameField> = [];
        const data:FrameData = [];

        for( let i = 0; i < numRows; i++ )
            data[i] = [];

        fieldNames.map((fieldName:FieldName, colIndex:RowIndex) =>
        {
            const oColumn = parsedJsonObject[fieldName];
            const oColumnType:string = oColumn[Type.TYPE_KEY];
            const oColumnRowValues = oColumn.rows;

            const fieldType = Type.fromTypeName(oColumnType);

            fields.push(new FrameField(fieldName, fieldType));

            let rowIndex:RowIndex = 0;

            for( let cell of oColumnRowValues )
            {
                data[rowIndex][colIndex] = Type.deserializeIValue(cell);

                rowIndex++;
            }
        });

        const metadata:Map<FrameMetaKey, FrameMetaValue> = new Map<FrameMetaKey, FrameMetaValue>();

        const oMeta:Object = parsedJsonObject[Frames.META_KEY];

        if( oMeta )
            Object.keys(oMeta).forEach(
                (metaKey:FrameMetaKey) => metadata.set(metaKey, oMeta[metaKey] && oMeta[metaKey].toString())
            );

        return new AFrame(fields, data, new FrameMeta(metadata));
    }

    public static toJSON(frame:IFrame):Object
    {
        if( !frame )
            return null;

        const fields:Array<FrameField> = frame.getFields();
        const o:Object = {};

        const {serialize} = require('@mintdata/mint-kit/impl/util/serializr/src/serializr');

        for( let field of fields )
        {
            const oColumn:Object = {};

            oColumn[Type.TYPE_KEY] = field.type.toString().toUpperCase();
            const oRows:Array<any> = oColumn['rows'] = [];

            const column = frame.getColumn(field.name);

            for( let cell of column.rows )
                oRows.push(serialize(cell));

            o[field.name] = oColumn;
        }

        const oMeta:Object = {};

        const meta:FrameMeta = frame.getMeta();
        const metaKeys:Array<FrameMetaKey> = meta && meta.getKeys() || [];

        for( let metaKey of metaKeys )
            oMeta[metaKey] = meta.getValue(metaKey);

        o[Type.TYPE_KEY] = Type.Frame.toString().toUpperCase();
        o[Frames.META_KEY] = oMeta;

        return o;
    }

    public static forError<T>(errorText:string):IFrame
    {
        return Frames.fromFieldsAndData([FrameField.newErrorField('error')], [[AString.fromString(errorText)]]);
    }

    public static forErrorWithContext<T>(errorText:AError, errorContext:string):IFrame
    {
        return Frames.fromFieldsAndData(
            [new FrameField('error', Type.String), new FrameField('details', Type.String)],
            [[AString.fromString(errorText.error), AString.fromString(errorContext || '')]]
        );
    }

    public static fromFieldsAndData<T>(fields:Array<FrameField>, data:FrameData, meta?:FrameMeta):IFrame
    {
        return new AFrame(fields, data, meta);
    }

    public static fromStringsWithHeaders<T>(headersAndValues:Array<Array<string>>, meta?:FrameMeta):IFrame
    {
        let headers = headersAndValues.slice(0, 1) as any;
        let values = headersAndValues.slice(1, headersAndValues.length - 1);

        return Frames.fromStrings(headers, values, meta);
    }

    public static fromStrings<T>(headers:Array<string>, values:Array<Array<string | AError>>, meta?:FrameMeta):IFrame
    {
        let fields:Array<FrameField> = [];
        for( let header of headers )
            fields.push(new FrameField(header, Type.String));

        let data:FrameData = [];

        let i = 0;
        for( let rawRow of values )
        {
            let row:any = data[i] = [];

            for( let rawValue of rawRow )
            {
                if( Type.isInstanceOf(rawValue, Type.Error) )
                    row.push(rawValue);
                else
                    row.push(AString.fromString(rawValue as string));
            }

            i++;
        }

        return new AFrame(fields, data, meta);
    }

    public static framePathToString(pathInFrame:FramePath):string
    {
        if( !pathInFrame )
            return '';

        return pathInFrame
            .map(coord => `${coord.fieldName}[${coord.rowIndex}]`)
            .join('.');
    }

    // ! "strict" means that equal paths are not considered to be prefix of each other
    public static isStrictFramePathPrefix(prefixCandidate:FramePath, fullPath:FramePath):boolean
    {
        return prefixCandidate.length < fullPath.length &&
            prefixCandidate.every((coord, index) => coord.isEqualTo(fullPath[index]));
    }

    public static join(frame1:IFrame,
                       joinFieldName1:FieldName,
                       frame2:IFrame,
                       joinFieldName2:FieldName = joinFieldName1,
                       frame1Alias:string = 'left',
                       frame2Alias:string = 'right'):IFrame | AError
    {
        return this._joinImpl(frame1, joinFieldName1, frame2, joinFieldName2, frame1Alias, frame2Alias, JoinType.INNER);
    }

    public static joinLeft(frame1:IFrame,
                           joinFieldName1:FieldName,
                           frame2:IFrame,
                           joinFieldName2:FieldName = joinFieldName1,
                           frame1Alias:string = 'left',
                           frame2Alias:string = 'right'):IFrame | AError
    {
        return this._joinImpl(frame1, joinFieldName1, frame2, joinFieldName2, frame1Alias, frame2Alias, JoinType.LEFT);
    }

    public static joinRight(frame1:IFrame,
                            joinFieldName1:FieldName,
                            frame2:IFrame,
                            joinFieldName2:FieldName = joinFieldName1,
                            frame1Alias:string = 'left',
                            frame2Alias:string = 'right'):IFrame | AError
    {
        return this._joinImpl(frame1, joinFieldName1, frame2, joinFieldName2, frame1Alias, frame2Alias, JoinType.RIGHT);
    }

    public static joinFull(frame1:IFrame,
                           joinFieldName1:FieldName,
                           frame2:IFrame,
                           joinFieldName2:FieldName = joinFieldName1,
                           frame1Alias:string = 'left',
                           frame2Alias:string = 'right'):IFrame | AError
    {
        return this._joinImpl(frame1, joinFieldName1, frame2, joinFieldName2, frame1Alias, frame2Alias, JoinType.FULL);
    }

    public static joinCross(frame1:IFrame,
                            frame2:IFrame,
                            frame1Alias:string = 'left',
                            frame2Alias:string = 'right'):IFrame | AError
    {
        const fields1 = frame1.getFieldsUnsafe();
        const fields2 = frame2.getFieldsUnsafe();
        const resultFields = this._collectJoinFramesFields(fields1, fields2, frame1Alias, frame2Alias);

        const data1 = frame1.getDataUnsafe();
        const data2 = frame2.getDataUnsafe();

        const resultData = [];
        data1.forEach(row1 =>
            data2.forEach(row2 =>
                resultData.push([...row1, ...row2])
            )
        );

        return new AFrame(resultFields, resultData);
    }

    private static _joinImpl(frame1:IFrame,
                             joinFieldName1:FieldName,
                             frame2:IFrame,
                             joinFieldName2:FieldName = joinFieldName1,
                             frame1Alias:string = 'left',
                             frame2Alias:string = 'right',
                             joinType:JoinType)
    {
        const collectKeys = (data, joinFieldIndex) =>
        {
            const keys = [];
            for( const row of data )
            {
                const value = row[joinFieldIndex];
                const coerced = CoercionUtils.coerce(value, Type.String, null);
                if( Type.isInstanceOf(coerced, Type.Error) )
                    return new AError('#CANT_COERCE_VALUE_TO_STRING');

                const joinKey = (coerced as AString).value;
                keys.push(joinKey);
            }

            return keys;
        };

        const createJoinedRowFn = (row1, row2) =>
        {
            const row = [];
            if( row1 )
                row.push(
                    row1[joinFieldIndex1],
                    ...row1.slice(0, joinFieldIndex1),
                    ...row1.slice(joinFieldIndex1 + 1)
                );
            else
                row.push(
                    row2[joinFieldIndex2],
                    ...AEmpty.array(fields1.length - 1)
                );

            if( row2 )
                row.push(
                    ...row2.slice(0, joinFieldIndex2),
                    ...row2.slice(joinFieldIndex2 + 1)
                );
            else
                row.push(
                    ...AEmpty.array(fields2.length)
                );

            return row;
        };

        const fields1 = frame1.getFieldsUnsafe();
        const fields2 = frame2.getFieldsUnsafe();

        const joinFieldIndex1 = fields1.findIndex(f => f.name === joinFieldName1);
        const joinFieldIndex2 = fields2.findIndex(f => f.name === joinFieldName2);
        if( joinFieldIndex1 === -1 || joinFieldIndex2 === -1 )
            return new AError('#COLUMN_NOT_FOUND');

        const joinedFields1 = [fields1[joinFieldIndex1], ...fields1.filter((f, i) => i !== joinFieldIndex1)];
        const joinedFields2 = fields2.filter((f, i) => i !== joinFieldIndex2);
        const resultFields = this._collectJoinFramesFields(joinedFields1, joinedFields2, frame1Alias, frame2Alias);

        const data1 = frame1.getDataUnsafe();
        const data2 = frame2.getDataUnsafe();

        const keysOrError1 = collectKeys(data1, joinFieldIndex1);
        if( Type.isInstanceOf(keysOrError1, Type.Error) )
            return keysOrError1 as AError;

        const keysOrError2 = collectKeys(data2, joinFieldIndex2);
        if( Type.isInstanceOf(keysOrError2, Type.Error) )
            return keysOrError2 as AError;

        const keys1 = keysOrError1 as Array<string>;
        const keys2 = keysOrError2 as Array<string>;
        const groups2 = ArrayUtils.groupArray(data2, (row, index) => keys2[index]);

        // iterate first frame and add all matching joins
        const resultData = [];
        keys1.forEach((key, i) =>
        {
            let group2 = groups2[key];
            if( !group2 && joinType !== JoinType.LEFT && joinType !== JoinType.FULL )
                return;

            if( !group2 )
                group2 = [null];

            const row1 = data1[i];
            group2.forEach(row2 =>
                resultData.push(createJoinedRowFn(row1, row2))
            );
        });

        // add unmatched rows from 2nd frame
        if( joinType === JoinType.FULL || joinType === JoinType.RIGHT )
        {
            const keyMap1 = ArrayUtils.arrayToMap(keys1, k => k);
            keys2.forEach((key2, i) =>
            {
                if( !keyMap1[key2] )
                    resultData.push(createJoinedRowFn(null, data2[i]));
            });
        }

        return new AFrame(resultFields, resultData);
    }

    private static _collectJoinFramesFields(fields1:Array<FrameField>, fields2:Array<FrameField>, alias1:string, alias2:string):Array<FrameField>
    {
        const fieldsAttrs1 = fields1.map(field => ({
            name: field.name,
            newName: field.name,
            type: field.type
        }));
        const fieldsAttrs2 = fields2.map(field => ({
            name: field.name,
            newName: field.name,
            type: field.type
        }));

        // left/right conflict resolution logic
        fieldsAttrs1.forEach(field =>
        {
            const conflictField = fieldsAttrs2.find(f => f.name === field.name);
            if( conflictField )
            {
                field.newName = alias1 + '_' + field.name;
                conflictField.newName = alias2 + '_' + conflictField.name;
            }
        });

        // "counter" resolution logic for cases when we already have columns like `left_name`, `right_name`, etc..
        const resultFieldsMap = {};
        const mapWithConflictsFn = (fieldAttrs):Array<FrameField> =>
        {
            return fieldAttrs.map(field =>
            {
                let newName = field.newName;
                let counter = 1;
                while( resultFieldsMap[newName] )
                {
                    counter++;
                    newName = field.newName + '_' + counter;
                }

                return new FrameField(newName, field.type);
            });
        };

        return [
            ...mapWithConflictsFn(fieldsAttrs1),
            ...mapWithConflictsFn(fieldsAttrs2)
        ];
    }
}
